/**
 * File types supported by Lens document intake.
 */

import type { LensFilePayload } from '@/types/lens-api'
import {
  LENS_MAX_ANALYSIS_WORDS,
  LENS_MAX_FILE_BYTES,
  LENS_MAX_SITE_PAGES,
} from '@lina/lens-limits'

export type { LensFilePayload }

export { LENS_MAX_ANALYSIS_WORDS, LENS_MAX_FILE_BYTES, LENS_MAX_SITE_PAGES }

/** Extensions parsed server-side (binary / rich documents). */
export const LENS_BINARY_EXTENSIONS = new Set([
  'pdf',
  'doc',
  'docx',
  'dot',
  'odt',
  'rtf',
  'pot',
  'ppa',
  'pps',
  'ppt',
  'pptx',
  'pwz',
  'wiz',
  'xla',
  'xlb',
  'xlc',
  'xlm',
  'xls',
  'xlsx',
  'xlt',
  'xlw',
  'iif',
])

/** All extensions accepted for Lens uploads (matches OpenAI input_file list). */
export const LENS_SUPPORTED_EXTENSIONS = new Set([
  ...LENS_BINARY_EXTENSIONS,
  'asm',
  'bat',
  'c',
  'cc',
  'conf',
  'cpp',
  'css',
  'cxx',
  'csv',
  'def',
  'dic',
  'eml',
  'h',
  'hh',
  'htm',
  'html',
  'ics',
  'ifb',
  'in',
  'js',
  'json',
  'ksh',
  'list',
  'log',
  'markdown',
  'md',
  'mht',
  'mhtml',
  'mime',
  'mjs',
  'nws',
  'pl',
  'py',
  'rst',
  's',
  'sql',
  'srt',
  'text',
  'tsv',
  'txt',
  'vcf',
  'vtt',
  'xml',
])

const EXTENSION_TO_MIME: Record<string, string> = {
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  dot: 'application/msword',
  odt: 'application/vnd.oasis.opendocument.text',
  rtf: 'application/rtf',
  pot: 'application/vnd.ms-powerpoint',
  ppa: 'application/vnd.ms-powerpoint',
  pps: 'application/vnd.ms-powerpoint',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  pwz: 'application/vnd.ms-powerpoint',
  wiz: 'application/vnd.ms-powerpoint',
  xla: 'application/vnd.ms-excel',
  xlb: 'application/vnd.ms-excel',
  xlc: 'application/vnd.ms-excel',
  xlm: 'application/vnd.ms-excel',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  xlt: 'application/vnd.ms-excel',
  xlw: 'application/vnd.ms-excel',
  csv: 'text/csv',
  tsv: 'text/tab-separated-values',
  iif: 'application/x-iif',
  txt: 'text/plain',
  text: 'text/plain',
  md: 'text/markdown',
  markdown: 'text/markdown',
  html: 'text/html',
  htm: 'text/html',
  json: 'application/json',
  xml: 'text/xml',
  css: 'text/css',
  js: 'text/javascript',
  mjs: 'text/javascript',
  py: 'text/x-python',
  sql: 'application/x-sql',
  eml: 'message/rfc822',
  ics: 'text/calendar',
  srt: 'text/srt',
  vtt: 'text/vtt',
  vcf: 'text/x-vcard',
}

export function getLensFileExtension(fileName: string): string {
  const base = fileName.split(/[/\\]/).pop() ?? fileName
  const dot = base.lastIndexOf('.')
  if (dot <= 0 || dot === base.length - 1) return ''
  return base.slice(dot + 1).toLowerCase()
}

export function isLensBinaryFileName(fileName: string): boolean {
  return LENS_BINARY_EXTENSIONS.has(getLensFileExtension(fileName))
}

export function isLensSupportedFileName(fileName: string): boolean {
  return LENS_SUPPORTED_EXTENSIONS.has(getLensFileExtension(fileName))
}

export function isLensSupportedFile(file: File): boolean {
  if (isLensSupportedFileName(file.name)) return true
  if (file.type && Object.values(EXTENSION_TO_MIME).includes(file.type)) return true
  return false
}

export function isLensBinaryFile(file: File): boolean {
  return isLensBinaryFileName(file.name)
}

export function getLensMimeType(file: File): string {
  if (file.type) return file.type
  const ext = getLensFileExtension(file.name)
  return EXTENSION_TO_MIME[ext] ?? 'application/octet-stream'
}

export function getLensAcceptAttribute(): string {
  const extensions = [...LENS_SUPPORTED_EXTENSIONS].map((ext) => `.${ext}`)
  const mimeTypes = [...new Set(Object.values(EXTENSION_TO_MIME))]
  return [...extensions, ...mimeTypes].join(',')
}

/**
 * Curated file-picker filter for Lens upload UIs (product + admin).
 * Subset of {@link LENS_SUPPORTED_EXTENSIONS}; keep in sync when adding common document types.
 */
export const LENS_DOCUMENT_PICKER_ACCEPT =
  '.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.md,.csv,.html,.htm,text/html'

export function formatLensSupportedTypesSummary(): string {
  return 'PDF, Word, PowerPoint, Excel/CSV, HTML, and plain text or code files (up to 50 MB)'
}

export async function readLensTextFile(file: File): Promise<string> {
  return file.text()
}

export async function readLensFileAsBase64(file: File): Promise<LensFilePayload> {
  const buffer = await file.arrayBuffer()
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.length; i += 8192) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 8192))
  }
  return {
    name: file.name,
    mime_type: getLensMimeType(file),
    data: btoa(binary),
  }
}

export function estimateBase64ByteSize(base64: string): number {
  const normalized = base64.replace(/^data:[^;]+;base64,/, '').replace(/\s/g, '')
  const padding = normalized.endsWith('==') ? 2 : normalized.endsWith('=') ? 1 : 0
  return Math.floor((normalized.length * 3) / 4) - padding
}
